import { runtimeConfig } from "../config/runtime";
import { eventsDB, ParsedEvent } from "../db/eventsDb";

// Dispatch custom event when events are updated
const dispatchEventsUpdated = () => {
  window.dispatchEvent(new CustomEvent("events-updated"));
};

// Add deduplication cache for in-flight requests
const pendingRequests = new Map<string, Promise<ParsedEvent[]>>();
const DEDUPLICATION_TTL = 5000; // 5 seconds

// Cache of masjids whose events.ics returned 404 — avoids hammering for files that don't exist
const notFoundCache = new Map<number, number>();
const NOT_FOUND_COOLDOWN_MS = 6 * 60 * 60 * 1000; // 6 hours

// Add online status detection
let isOnline = navigator.onLine;
window.addEventListener("online", () => {
  isOnline = true;
});
window.addEventListener("offline", () => {
  isOnline = false;
});

// Parse ICS date format (YYYYMMDD or YYYYMMDDTHHmmssZ or with TZID)
function parseICSDate(value: string, fullKey: string): Date {
  // Remove any timezone suffix for parsing
  let dateStr = value.replace("Z", "");

  if (dateStr.length === 8) {
    // Date only: YYYYMMDD
    const year = parseInt(dateStr.substring(0, 4));
    const month = parseInt(dateStr.substring(4, 6)) - 1;
    const day = parseInt(dateStr.substring(6, 8));
    // For date-only, create at start of day in local time
    return new Date(year, month, day);
  } else if (dateStr.length >= 15) {
    // Date-time: YYYYMMDDTHHmmss
    const year = parseInt(dateStr.substring(0, 4));
    const month = parseInt(dateStr.substring(4, 6)) - 1;
    const day = parseInt(dateStr.substring(6, 8));
    const hour = parseInt(dateStr.substring(9, 11));
    const minute = parseInt(dateStr.substring(11, 13));
    const second = parseInt(dateStr.substring(13, 15));

    // If it ends with Z, it's UTC
    if (value.endsWith("Z")) {
      return new Date(Date.UTC(year, month, day, hour, minute, second));
    }
    // If it has TZID, treat as local time (assuming screen is in same timezone as ICS)
    else if (fullKey.includes("TZID")) {
      return new Date(year, month, day, hour, minute, second);
    }

    return new Date(year, month, day, hour, minute, second);
  }

  return new Date(value);
}

// Unescape ICS text
function unescapeICS(text: string): string {
  return text
    .replace(/\\n/g, "\n")
    .replace(/\\,/g, ",")
    .replace(/\\;/g, ";")
    .replace(/\\\\/g, "\\");
}

// Simple ICS parser - extracts events from ICS text
function parseICS(icsText: string): ParsedEvent[] {
  const events: ParsedEvent[] = [];
  const lines = icsText.split(/\r?\n/);

  let currentEvent: Partial<ParsedEvent> | null = null;
  let inEvent = false;

  for (let i = 0; i < lines.length; i++) {
    let line = lines[i];

    // Handle line folding (lines that start with space/tab are continuations)
    while (
      i + 1 < lines.length &&
      (lines[i + 1].startsWith(" ") || lines[i + 1].startsWith("\t"))
    ) {
      i++;
      line += lines[i].substring(1);
    }

    if (line.startsWith("BEGIN:VEVENT")) {
      inEvent = true;
      currentEvent = {};
    } else if (line.startsWith("END:VEVENT") && currentEvent) {
      if (currentEvent.uid && currentEvent.summary && currentEvent.start) {
        events.push(currentEvent as ParsedEvent);
      }
      currentEvent = null;
      inEvent = false;
    } else if (inEvent && currentEvent) {
      // Parse properties
      const colonIndex = line.indexOf(":");
      if (colonIndex > 0) {
        const fullKey = line.substring(0, colonIndex);
        const value = line.substring(colonIndex + 1);
        const key = fullKey.split(";")[0]; // Remove parameters like TZID

        switch (key) {
          case "UID":
            currentEvent.uid = value;
            break;
          case "SUMMARY":
            currentEvent.summary = unescapeICS(value);
            break;
          case "DESCRIPTION":
            currentEvent.description = unescapeICS(value);
            // Extract speaker information from description if present
            // Use lookahead to stop at escaped newline (\n) or end of string
            const speakerMatch = value.match(/Speakers?:\s*(.+?)(?=\\n|$)/i);
            if (speakerMatch) {
              currentEvent.speakers = unescapeICS(speakerMatch[1].trim());
            }
            // Extract audience information from description if present
            // Use lookahead to stop at escaped newline (\n) or end of string
            const audienceMatch = value.match(/Audience?:\s*(.+?)(?=\\n|$)/i);
            if (audienceMatch) {
              currentEvent.audience = unescapeICS(audienceMatch[1].trim());
            }
            break;
          case "LOCATION":
            currentEvent.location = unescapeICS(value);
            break;
          case "DTSTART": {
            const date = parseICSDate(value, fullKey);
            currentEvent.start = date.toISOString();
            currentEvent.allDay =
              fullKey.includes("VALUE=DATE") && !fullKey.includes("DATE-TIME");
            break;
          }
          case "DTEND": {
            const date = parseICSDate(value, fullKey);
            currentEvent.end = date.toISOString();
            break;
          }
        }
      }
    }
  }

  return events;
}

// Interface for hydrated events with Date objects (for use in components)
export interface HydratedEvent {
  uid: string;
  summary: string;
  description?: string;
  location?: string;
  speakers?: string;
  audience?: string;
  start: Date;
  end?: Date;
  allDay?: boolean;
  // Shown instead of the start time, e.g. "After Maghrib" (Labbaik salah-time events)
  timeLabel?: string;
}

// Convert stored events (ISO strings) back to Date objects for use in components
export function hydrateEvents(storedEvents: ParsedEvent[]): HydratedEvent[] {
  return storedEvents.map((event) => ({
    ...event,
    start: new Date(event.start),
    end: event.end ? new Date(event.end) : undefined,
  }));
}

/**
 * Fetch events ICS file with offline-first caching
 * Uses the same pattern as timetable/prayer times fetching
 *
 * @param masjidId - The masjid ID
 * @param refreshIntervalSeconds - How often to check for new events (uses screenData.timesRefreshInterval)
 * @param forceRefresh - Force a network fetch even if cache is fresh
 */
// The upcoming-events widget's rule: starts within the next 7 days and hasn't finished yet.
export function hasUpcomingEvents(events: HydratedEvent[], now = new Date()): boolean {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  const weekEnd = new Date(today);
  weekEnd.setDate(today.getDate() + 7);
  return events.some(
    (e) =>
      e.start >= today &&
      e.start < weekEnd &&
      (e.start >= tomorrow || e.allDay || e.start >= now || (!!e.end && e.end > now)),
  );
}

export async function fetchEventsICS(
  masjidId: number,
  refreshIntervalSeconds: number = 300,
  forceRefresh: boolean = false,
): Promise<ParsedEvent[]> {
  const cacheKey = `events:${masjidId}`;

  // Check if there's already a pending request for this masjid
  if (!forceRefresh && pendingRequests.has(cacheKey)) {
    console.log(`♻️ Reusing in-flight request for events (masjid ${masjidId})`);
    return pendingRequests.get(cacheKey) as Promise<ParsedEvent[]>;
  }

  const fetchPromise = (async () => {
    try {
      const now = Date.now();
      const refreshIntervalMs = refreshIntervalSeconds * 1000;

      // Check IndexedDB cache first
      const cached = await eventsDB.getEvents(masjidId);

      if (cached && !forceRefresh) {
        const timeSinceLastFetch = now - cached.lastFetch;

        if (timeSinceLastFetch < refreshIntervalMs) {
          console.log(
            `📚 Using cached events for masjid ${masjidId} (${Math.round(timeSinceLastFetch / 1000)}s old)`,
          );
          return cached.events;
        }
      }

      // If offline, return cached data if available
      if (!isOnline) {
        console.log(
          `📴 Device offline, using cached events for masjid ${masjidId}`,
        );
        if (cached) {
          return cached.events;
        }
        console.warn(`No cached events available for masjid ${masjidId}`);
        return [];
      }

      // Skip network fetch if this masjid recently returned 404
      const lastNotFound = notFoundCache.get(masjidId);
      if (lastNotFound && Date.now() - lastNotFound < NOT_FOUND_COOLDOWN_MS) {
        console.log(
          `📭 Skipping events fetch for masjid ${masjidId}: returned 404 recently, cooldown active`,
        );
        return cached?.events || [];
      }

      // Fetch from network (service worker will cache this)
      console.log(`🌐 Fetching events ICS for masjid ${masjidId}`);
      const icsUrl = `${runtimeConfig.storageUrl}/jsonfiles/events/${masjidId}/events.ics`;

      const response = await fetch(icsUrl, {
        cache: "no-cache", // Let service worker handle caching
        signal: AbortSignal.timeout(10000),
      });

      if (!response.ok) {
        if (response.status === 404) {
          // No events file exists - this is OK, just return empty and cache it
          console.log(`📭 No events file for masjid ${masjidId}`);
          notFoundCache.set(masjidId, Date.now());
          await eventsDB.saveEvents(masjidId, []);
          dispatchEventsUpdated();
          return [];
        }
        throw new Error(`Failed to fetch events: ${response.status}`);
      }

      const eTag = response.headers.get("ETag") || undefined;
      const icsText = await response.text();
      const parsedEvents = parseICS(icsText);

      // Sort by start date
      parsedEvents.sort(
        (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime(),
      );

      // Save to IndexedDB cache
      await eventsDB.saveEvents(masjidId, parsedEvents, eTag);
      dispatchEventsUpdated();

      console.log(
        `✅ Fetched and cached ${parsedEvents.length} events for masjid ${masjidId}`,
      );
      return parsedEvents;
    } catch (error) {
      console.error(`Error fetching events for masjid ${masjidId}:`, error);

      // Fallback to cached data on error
      const cached = await eventsDB.getEvents(masjidId);
      if (cached) {
        console.log(
          `⚠️ Using stale cached events for masjid ${masjidId} after fetch error`,
        );
        return cached.events;
      }

      // Return empty array if no cache available
      return [];
    } finally {
      // Remove from pending requests after a delay
      setTimeout(() => {
        pendingRequests.delete(cacheKey);
      }, DEDUPLICATION_TTL);
    }
  })();

  // Store the promise for potential reuse
  pendingRequests.set(cacheKey, fetchPromise);
  return fetchPromise;
}

/**
 * Clear cached events for a specific masjid
 */
export async function clearEventsCache(masjidId: number): Promise<void> {
  await eventsDB.clearMasjidEvents(masjidId);
}

/**
 * Clear all cached events
 */
export async function clearAllEventsCache(): Promise<void> {
  await eventsDB.clearAll();
}
